import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// 12 real rose-petal silhouettes extracted from a photo, recolored white
// while preserving natural per-pixel luminance shading and edge softness.
// Served from /public/petals/pN.png.
const PETAL_URLS = Array.from({ length: 12 }, (_, i) => `/petals/p${i + 1}.png`);

export const RosePetalsIntro = ({ count = 250, duration = 7.5 }) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setShow(false); return undefined; }
    const t = setTimeout(() => setShow(false), (duration + 0.8) * 1000);
    return () => clearTimeout(t);
  }, [duration]);

  const petals = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 22 + Math.random() * 42, // 22-64px — bigger since these are real photos
        delay: Math.random() * 4,
        drift: -80 + Math.random() * 160,
        startRot: Math.random() * 360,
        spin: -900 + Math.random() * 1800,
        speed: duration * (0.55 + Math.random() * 0.7),
        src: PETAL_URLS[i % PETAL_URLS.length],
        flip: Math.random() < 0.5,
      })),
    [count, duration],
  );

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="petals"
          aria-hidden
          className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          {petals.map((p) => (
            <motion.img
              key={p.id}
              src={p.src}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: `${p.left}vw`,
                top: "-8vh",
                width: `${p.size}px`,
                height: "auto",
                transform: p.flip ? "scaleX(-1)" : undefined,
                filter: "drop-shadow(0 2px 3px rgba(60,45,20,0.18))",
              }}
              initial={{ y: 0, x: 0, rotate: p.startRot, opacity: 0 }}
              animate={{
                y: ["-8vh", "108vh"],
                x: [0, p.drift, p.drift * 0.7],
                rotate: [p.startRot, p.startRot + p.spin * 0.5, p.startRot + p.spin],
                opacity: [0, 1, 1, 0.95, 0],
              }}
              transition={{
                duration: p.speed,
                delay: p.delay,
                ease: "easeIn",
                times: [0, 0.05, 0.6, 0.85, 1],
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
